import { useCallback } from "react";
import { Task } from "@common/types/task";
import { DocumentMigrationErrorCode } from "@common/constants/accounting-migration";
import {
    DocumentMigrationError,
    documentMigrationErrorToastMessage,
    MIGRATION_IMPORT_FAILED_TOAST,
    type SubmitDocumentMigrationParams,
    useSubmitDocumentMigrationMutation,
} from "@api/accountingIntegration.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import type { CoADocumentMigrationSource } from "@common/schemas/accounting-integration.schema";
import { useCompleteMigrationTask } from "./useCompleteMigrationTask";

interface UseMigrationUploadMigrateParams {
    workspaceId: string;
    entityId: number;
    source: CoADocumentMigrationSource;
    selectedTask: Task;
    migrationDocumentIds: SubmitDocumentMigrationParams | null;
    refetchValidation: () => void;
    onFinalizeWarningNeeded: () => void;
    hasFinalizeWarnings: boolean;
}

export function useMigrationUploadMigrate({
    workspaceId,
    entityId,
    source,
    selectedTask,
    migrationDocumentIds,
    refetchValidation,
    onFinalizeWarningNeeded,
    hasFinalizeWarnings,
}: UseMigrationUploadMigrateParams) {
    const { error: toastError, warning: toastWarning } = useToaster();
    const completeMigrationTask = useCompleteMigrationTask(selectedTask);

    const migrateMutation = useSubmitDocumentMigrationMutation({
        workspaceId,
        entityId,
        source,
    });

    const handleMigrate = useCallback(() => {
        if (migrationDocumentIds === null) {
            return;
        }

        migrateMutation.mutate(migrationDocumentIds, {
            onSuccess: (body) => {
                const droppedWarning = body.warnings?.[0];
                if (droppedWarning) {
                    toastWarning(droppedWarning);
                }
                completeMigrationTask();
            },
            onError: (error) => {
                const isCoaCrossFileRejection =
                    error instanceof DocumentMigrationError &&
                    error.detail.code ===
                        DocumentMigrationErrorCode.IMPORT_REJECTED &&
                    error.detail.file === "coa";
                const isValidationIssue =
                    error instanceof DocumentMigrationError &&
                    (error.detail.code ===
                        DocumentMigrationErrorCode.ACCOUNT_CODE_MISMATCH ||
                        error.detail.code ===
                            DocumentMigrationErrorCode.UNBALANCED_JOURNAL_ENTRIES);
                if (isValidationIssue || isCoaCrossFileRejection) {
                    refetchValidation();
                    return;
                }
                toastError(
                    documentMigrationErrorToastMessage(
                        error,
                        MIGRATION_IMPORT_FAILED_TOAST,
                    ),
                );
            },
        });
    }, [
        migrationDocumentIds,
        migrateMutation,
        completeMigrationTask,
        toastError,
        toastWarning,
        refetchValidation,
    ]);

    const handleFinalizeClick = useCallback(() => {
        if (hasFinalizeWarnings) {
            onFinalizeWarningNeeded();
            return;
        }
        handleMigrate();
    }, [hasFinalizeWarnings, onFinalizeWarningNeeded, handleMigrate]);

    return {
        migrateMutation,
        handleMigrate,
        handleFinalizeClick,
    };
}
